import { createTheme } from '@mui/material/styles';
import { colors } from './colors';

// Material-UI theme based on AI Listing Generator color system
export const theme = createTheme({
  palette: {
    // Primary brand color
    primary: {
      main: colors.primary.main,
      dark: colors.primary.hover,
      light: colors.primary.light,
      contrastText: '#FFFFFF',
    },

    // Secondary color (using medium gray)
    secondary: {
      main: colors.text.tertiary,
      dark: colors.text.secondary,
      light: colors.background.medium,
      contrastText: '#FFFFFF',
    },

    // CTA colors (for primary actions)
    cta: {
      main: colors.cta.main,
      dark: colors.cta.hover,
      light: colors.cta.light,
      contrastText: '#FFFFFF',
    },

    // Error colors
    error: {
      main: colors.status.error.main,
      dark: colors.status.error.dark,
      light: colors.status.error.light,
      contrastText: '#FFFFFF',
    },

    // Warning colors
    warning: {
      main: colors.status.warning.main,
      light: colors.status.warning.light,
      contrastText: '#FFFFFF',
    },

    // Success colors
    success: {
      main: colors.status.success.main,
      light: colors.status.success.light,
      contrastText: '#FFFFFF',
    },

    // Text colors
    text: {
      primary: colors.text.primary,
      secondary: colors.text.tertiary,
      disabled: colors.text.disabled,
    },

    // Background colors
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },

    // Divider color
    divider: colors.border.light,

    // Action colors (for buttons, etc.)
    action: {
      hover: colors.interactive.hover,
      disabled: colors.interactive.disabled,
      disabledBackground: colors.background.medium,
    },
  },

  // Typography customization
  typography: {
    fontFamily: '"Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    
    // Main page headers
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      color: colors.text.primary,
      lineHeight: 1.2,
    },
    
    // Section headers (standardized)
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: colors.text.primary,
      lineHeight: 1.3,
    },
    
    // Subsection headers
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: colors.text.primary,
      lineHeight: 1.4,
    },
    
    // Component headers
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: colors.text.primary,
      lineHeight: 1.4,
    },
    
    // Small headers
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.text.primary,
      lineHeight: 1.5,
    },
    
    // Legacy support (now maps to h5)
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.text.primary,
      lineHeight: 1.5,
    },

    // Primary body text
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      color: colors.text.secondary,
      lineHeight: 1.5,
    },
    
    // Secondary body text
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      color: colors.text.tertiary,
      lineHeight: 1.4,
    },

    // Large subtitle
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      color: colors.text.secondary,
      lineHeight: 1.5,
    },
    
    // Small subtitle/labels
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: colors.text.secondary,
      lineHeight: 1.4,
    },

    // Small text/metadata
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      color: colors.text.placeholder,
      lineHeight: 1.3,
    },
    
    // Button text
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      lineHeight: 1.4,
    },
    
    // Overline text (chips, tags)
    overline: {
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      lineHeight: 1.2,
    },
  },

  // Component customizations
  components: {
    // Button customizations
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Remove uppercase transformation
          borderRadius: 8, // Consistent border radius
        },
        contained: {
          boxShadow: colors.shadows.none, // Use theme shadow system
          '&:hover': {
            boxShadow: colors.shadows.none,
          },
        },
      },
    },

    // Paper customizations
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Consistent border radius for cards
        },
      },
    },

    // TextField customizations
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: colors.background.white, // Default white background for all inputs
            '& fieldset': {
              borderColor: colors.border.light,
            },
            '&:hover fieldset': {
              borderColor: colors.border.medium,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
            },
          },
        },
      },
    },

    // Chip customizations
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          height: 24, // Consistent height from Step2
        },
      },
    },

    // DataGrid customizations
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.border.light}`,
          borderRadius: 8,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.background.light,
            borderBottom: `1px solid ${colors.border.light}`,
          },
          '& .MuiDataGrid-row': {
            borderBottom: `1px solid ${colors.background.medium}`,
            '&:hover': {
              backgroundColor: colors.background.light,
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${colors.border.light}`,
            backgroundColor: colors.background.light,
          },
        },
      },
    },

    // Card customizations
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${colors.border.light}`,
          boxShadow: colors.shadows.card,
        },
      },
    },

    // Switch customizations
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: colors.switch.track,
        },
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.switch.trackActive,
          },
        },
      },
    },
   },

  // Custom spacing (if needed)
  spacing: 8, // Default MUI spacing
});

// Custom theme extensions for specific use cases
export const themeExtensions = {
  // Custom colors not covered by MUI palette
  colors,
  
  // Spacing system (based on 8px grid)
  spacing: {
    xs: 1,    // 8px
    sm: 2,    // 16px
    md: 3,    // 24px (standard section spacing)
    lg: 4,    // 32px
    xl: 6,    // 48px
    xxl: 8,   // 64px
  },
  
  // Typography helpers
  typography: {
    // Semantic font weights
    weight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    
    // Common text styles
    styles: {
      sectionHeader: {
        variant: 'h2',
        sx: { mb: 3, fontWeight: 600 }
      },
      fieldLabel: {
        variant: 'subtitle2',
        sx: { mb: 1, fontWeight: 600 }
      },
      helpText: {
        variant: 'caption',
        sx: { color: 'text.secondary' }
      },
      errorText: {
        variant: 'caption',
        sx: { color: 'error.main' }
      }
    }
  },
  
  // Status colors helper
  getStatusColor: (status) => {
    switch (status) {
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  },

  // Chip colors helper
  getChipColors: (type) => {
    return colors.chip[type] || colors.chip.far;
  },
};
